// Business logic utilities
// State-dependent functions access centralized store

import { store } from '../store.js'
import { daysBetween, parseLocalDate, TODAY } from './date.js'
import { normalizePhone } from './format.js'
import { STAGES, M2M_THRESHOLD, PROXIMOS_DIAS } from '../constants.js'

export const getType = (dest, det) => {
    const d = String(dest || '').toUpperCase();
    const t = String(det || '').toUpperCase();
    if (d.includes('DCL') || d.includes('CRUCERO') || t.includes('CRUCERO')) return 'crucero';
    if (d.includes('DISNEY') || d.includes('WDW') || d.includes('DISNEYLAND')) return 'disney';
    if (d.includes('UNIVERSAL')) return 'universal';
    if ((t.includes('HOTEL') && !t.includes('ENTRADAS')) || d.includes('VRBO') || d.includes('BRECK')) return 'hotel';
    return 'otro';
};

// Calcular abono esperado según tipo de viaje
export const calcularAbonoEsperado = (tipo, totalVenta) => {
    const s = store?.settings || {};
    switch (tipo) {
        case 'disney': return s.abonoDisney || 200;
        case 'universal': return s.abonoUniversal || 200;
        case 'disney_universal': return s.abonoDisney || 200;
        case 'crucero': return Math.round((totalVenta || 0) * (s.abonoCrucero || 25) / 100);
        case 'parques_crucero': return Math.max(s.abonoDisney || 200, Math.round((totalVenta || 0) * (s.abonoCrucero || 25) / 100));
        case 'hotel': return s.abonoHotel || 100;
        case 'evento': return 100;
        default: return 100;
    }
};

// Calcular progreso según etapa
export const calcProgress = (stage) => {
    const progressMap = {
        'nuevo': 0,
        'formulario': 5,
        'asesoria_solicitada': 10,
        'asesoria_agendada': 15,
        'asesoria_realizada': 20,
        'proximo_ano': 22,
        'cotizacion_solicitada': 25,
        'cotizacion': 30,
        'revision_cotizacion': 35,
        'prereserva': 40,
        'reserva_abono': 60,
        'pago_completo': 80,
        'documentacion': 90,
        'en_destino': 95,
        'finalizado': 100,
        'postventa': 100,
        'no_califica': 0,
        'cancelado': 0,
        'no_compro': 0,
        'perdido': 100,
        'historico': 100
    };
    return progressMap[stage] || 0;
};

// Obtener índice de etapa para línea de progreso
export const getStageIndex = (stageId) => STAGES.findIndex(s => s.id === stageId);

// Determinar etapa para VENTAS según fechas y estado de pago
export const determineStageVentas = (rec) => {
    // Canceled trips (red row in Excel) → cancelado stage
    if (rec.canceled || rec.estadoPago === 'cancelado') return 'cancelado';

    const travelDate = parseLocalDate(rec.travelDate);
    const travelEnd = parseLocalDate(rec.travelEnd);

    // No dates = no booking = always nuevo
    if (!travelDate) return 'nuevo';

    const endDate = travelEnd || travelDate;
    const daysFromEnd = daysBetween(TODAY, endDate);

    // EN DESTINO: Hoy está entre fecha inicio y fecha fin (inclusive)
    if (TODAY >= travelDate && TODAY <= endDate) {
        return 'en_destino';
    }

    // Viajes terminados → historico o finalizado (regardless of payment status)
    if (daysFromEnd < -180) return 'historico';
    if (daysFromEnd < 0) return 'finalizado';

    // Viajes futuros - Usar el estado de pago
    if (rec.estadoPago === 'pago_completo') return 'pago_completo';
    if (rec.estadoPago === 'abono_pagado') return 'reserva_abono';

    // Default para registros sin info de abono
    return 'prereserva';
};

// Detectar viajeros frecuentes (2+ viajes separados por >30 días)
export const isFrequentTraveler = (client) => {
    if (!client.trips || client.trips.length < 2) return false;
    const trips = client.trips.filter(t => t.travelDate).map(t => new Date(t.travelDate)).sort((a, b) => a - b);
    if (trips.length < 2) return false;
    for (let i = 1; i < trips.length; i++) {
        if (Math.abs(daysBetween(trips[i - 1], trips[i])) > 30) return true;
    }
    return false;
};

// Calcular si es frecuente desde leads + histórico (todos los viajes)
export const isFrequentFromAllTrips = (allTrips) => {
    if (!allTrips || allTrips.length < 2) return false;
    const trips = allTrips.filter(t => t.travelDate).map(t => new Date(t.travelDate)).sort((a, b) => a - b);
    if (trips.length < 2) return false;
    for (let i = 1; i < trips.length; i++) {
        if (Math.abs(daysBetween(trips[i - 1], trips[i])) > 30) return true;
    }
    return false;
};

// Calcular comisión M2M total de un cliente (sumando trips + leads activos)
export const getClientM2MTotal = (client) => {
    let total = 0;
    if (client.trips) {
        total += client.trips.reduce((sum, t) => sum + (parseFloat(t.commission70) || 0), 0);
    }
    return total;
};

// Calcular M2M incluyendo leads activos por clientKey (only fully paid trips)
export const getClientM2MTotalFull = (clientKey) => {
    const { leads = [], historico = [] } = store || {};
    let total = 0;
    leads.filter(l => l.clientKey === clientKey && isFullyPaidTrip(l)).forEach(l => {
        total += parseFloat(l.commission70) || 0;
    });
    historico.filter(h => h.clientKey === clientKey && !h.wasCanceled).forEach(h => {
        total += parseFloat(h.commission70) || 0;
    });
    return total;
};

// Es viajero M2M (comisión > threshold)
export const isM2MTraveler = (client) => getClientM2MTotal(client) >= M2M_THRESHOLD;

// Stages that indicate a trip is fully paid (pago_completo or later in the pipeline)
const PAID_STAGES = ['pago_completo', 'documentacion', 'en_destino', 'finalizado', 'postventa'];

// Check if a lead/trip is fully paid (for M2M and Frecuentes views)
// Includes: PAID_STAGES or isHistorico (completed trips), excludes: cancelado/wasCanceled
export const isFullyPaidTrip = (lead) => {
    if (lead.stage === 'cancelado' || lead.stage === 'no_compro' || lead.wasCanceled) return false;
    if (lead.isHistorico) return true;
    return PAID_STAGES.includes(lead.stage);
};

// Próximos a viajar (viaja en los próximos N días, fully paid only)
// By 21 days out, all trips should be fully paid (30-day payment deadline).
export const isProximoAViajar = (lead) => {
    if (!lead.travelDate) return false;
    if (lead.stage === 'cancelado' || lead.stage === 'no_compro') return false;
    if (!PAID_STAGES.includes(lead.stage)) return false;
    const td = parseLocalDate(lead.travelDate);
    if (!td) return false;
    const days = daysBetween(TODAY, td);
    return days >= 0 && days <= PROXIMOS_DIAS;
};

// Buscar duplicados por teléfono
export const findDupByPhone = (phone) => {
    const n = normalizePhone(phone);
    if (!n) return null;
    const { clients = {} } = store || {};
    return Object.values(clients).find(c => c.phone === n);
};

// Buscar duplicados por email
export const findDupByEmail = (email) => {
    if (!email) return null;
    const e = email.toLowerCase().trim();
    const { clients = {} } = store || {};
    return Object.values(clients).find(c => c.email && c.email.toLowerCase() === e);
};

// Buscar duplicados por nombre
export const findDupByName = (name, type) => {
    if (!name || name.length < 3) return null;
    const n = name.toLowerCase().trim();
    const { clients = {} } = store || {};
    return Object.values(clients).find(c =>
        (c.titular && c.titular.toLowerCase() === n) ||
        (c.contacto && c.contacto.toLowerCase() === n)
    );
};

// Auto-calculate M2M commission (70%) from total commission
export function calcM2MFromTotal(prefix) {
    const commTotal = parseFloat(document.getElementById(prefix + 'CommTotal')?.value) || 0;
    const m2mField = document.getElementById(prefix + 'CommM2M');
    if (commTotal > 0 && m2mField) {
        const s = store?.settings || {};
        m2mField.value = parseFloat((commTotal * (s.comisionM2M || 70) / 100).toFixed(2));
    }
}

// Multi-hotel migration: old single l.hotel OR formData → l.hotels[0]
export function ensureHotelsArray(lead) {
    if (lead.hotels && Array.isArray(lead.hotels) && lead.hotels.length > 0) return lead.hotels
    const fd = lead.formData && typeof lead.formData === 'object' ? lead.formData : null
    const h = {}
    if (lead.hotel) h.name = lead.hotel
    else if (fd?.hotelEspecifico) h.name = fd.hotelEspecifico
    if (fd?.habitaciones) h.rooms = fd.habitaciones
    if (fd?.numNoches) h.notes = `${fd.numNoches} noches`
    if (lead.travelDate) h.checkIn = String(lead.travelDate).substring(0, 10)
    if (lead.travelEnd) h.checkOut = String(lead.travelEnd).substring(0, 10)
    h.reservationNum = ''
    h.type = ''
    h.cost = null
    lead.hotels = Object.keys(h).some(k => k !== 'reservationNum' && k !== 'type' && k !== 'cost' && h[k]) ? [h] : []
    return lead.hotels
}

// Multi-park migration: old single park fields OR formData → l.parkEntries[]
export function ensureParkEntriesArray(lead) {
    if (lead.parkEntries && Array.isArray(lead.parkEntries) && lead.parkEntries.length > 0) return lead.parkEntries
    const fd = lead.formData && typeof lead.formData === 'object' ? lead.formData : null
    const entries = []
    // First: try old single fields
    const p = {}
    if (lead.tipoParque) p.park = lead.tipoParque
    if (lead.diasParque) p.days = lead.diasParque
    if (lead.tickets) p.ticketType = lead.tickets
    if (lead.ticketValidFrom) p.validFrom = String(lead.ticketValidFrom).substring(0, 10)
    if (lead.ticketValidTo) p.validTo = String(lead.ticketValidTo).substring(0, 10)
    if (lead.reservationNum) p.reservationNum = lead.reservationNum
    p.parkHopper = lead.parkHopper || false
    p.diningPlan = lead.hasDiningPlan || false
    p.cost = null
    if (Object.keys(p).some(k => k !== 'parkHopper' && k !== 'diningPlan' && k !== 'cost' && p[k])) {
        entries.push(p)
    }
    // Fallback: populate from MagicForm formData fields
    if (entries.length === 0 && fd) {
        const marca = fd.parquesMarca // 'disney' | 'universal' | 'ambos'
        const DEST_MAP = { 'disney-world': 'Disney World', world: 'Disney World', 'disneyland-ca': 'Disneyland CA', 'universal-orlando': 'Universal Orlando', orlando: 'Universal Orlando', 'universal-hollywood': 'Universal Hollywood' }
        const hasDis = marca === 'disney' || marca === 'ambos'
        const hasUni = marca === 'universal' || marca === 'ambos'
        if (hasDis && (fd.diasDisney > 0 || fd.disneyDestino)) {
            entries.push({ park: DEST_MAP[fd.disneyDestino] || fd.disneyDestino || 'Disney World', days: parseInt(fd.diasDisney) || null, parkHopper: false, diningPlan: false, cost: null })
        }
        if (hasUni && (fd.diasUniversal > 0 || fd.universalDestino)) {
            entries.push({ park: DEST_MAP[fd.universalDestino] || fd.universalDestino || 'Universal Orlando', days: parseInt(fd.diasUniversal) || null, parkHopper: false, diningPlan: false, cost: null })
        }
    }
    lead.parkEntries = entries
    return lead.parkEntries
}

// Multi-cruise migration: solicitudCrucero OR formData → l.cruises[]
export function ensureCruisesArray(lead) {
    if (lead.cruises && Array.isArray(lead.cruises) && lead.cruises.length > 0) return lead.cruises
    const s = lead.solicitudCrucero
    const fd = lead.formData && typeof lead.formData === 'object' ? lead.formData : null
    const entries = []
    const port = (s?.puertos?.length ? (Array.isArray(s.puertos) ? s.puertos[0] : s.puertos) : null) ||
                 (fd?.puertos?.length ? (Array.isArray(fd.puertos) ? fd.puertos[0] : fd.puertos) : null)
    const cabin = s?.distribucionCabinas || fd?.cabina || ''
    const depDate = s?.fechaDeseada ? String(s.fechaDeseada).substring(0, 10) : null
    // Get cruise lines — naviera can be string (old) or array (MagicForm)
    let lines = []
    if (s?.naviera) lines = Array.isArray(s.naviera) ? s.naviera : [s.naviera]
    if (lines.length === 0 && fd?.naviera) lines = Array.isArray(fd.naviera) ? fd.naviera : [fd.naviera]
    // Filter out "Recomiéndame" — that's just context, not a specific line
    lines = lines.filter(l => l && l !== 'Recomiéndame')
    if (lines.length > 0) {
        lines.forEach(line => {
            entries.push({ line, departurePort: port || '', departureDate: depDate, cabin, reservationNum: '', cost: null, notes: '' })
        })
    } else if (port || cabin || depDate) {
        // Has cruise data but no specific line
        entries.push({ line: '', departurePort: port || '', departureDate: depDate, cabin, reservationNum: '', cost: null, notes: '' })
    }
    lead.cruises = entries
    return lead.cruises
}

// Sort array of objects chronologically by a date field
export function sortByDate(entries, dateField) {
    return entries.sort((a, b) => {
        const da = a[dateField] ? new Date(a[dateField]) : new Date('9999-12-31')
        const db = b[dateField] ? new Date(b[dateField]) : new Date('9999-12-31')
        return da - db
    })
}

// --- Auto-Transitions ---
// Full post-booking lifecycle based on travel dates:
//   pago_completo/documentacion -> en_destino (trip started)
//   en_destino -> finalizado (trip ended)
//   finalizado -> postventa (14 days after trip end)
//   postventa -> historico (180 days after trip end)

const DAYS_FINALIZADO_TO_POSTVENTA = 14;
const DAYS_TO_HISTORICO = 180;
const DAYS_SURVEY_DEADLINE = 2;

export async function runAutoTransitions() {
    const transitioned = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    for (const lead of [...store.leads]) {
        if (lead.stage === 'cancelado' || lead.stage === 'no_compro') continue;

        const travelDate = parseLocalDate(lead.travelDate);
        const travelEnd = parseLocalDate(lead.travelEnd);
        if (!travelDate) continue;

        const endDate = travelEnd || travelDate;
        const daysAfterEnd = daysBetween(endDate, now);

        let newStage = null;

        if (now >= travelDate && now <= endDate) {
            if (lead.stage === 'pago_completo' || lead.stage === 'documentacion') {
                newStage = 'en_destino';
            }
        } else if (daysAfterEnd > 0) {
            if (lead.stage === 'pago_completo' || lead.stage === 'documentacion' || lead.stage === 'en_destino') {
                newStage = 'finalizado';
            } else if (lead.stage === 'finalizado' && daysAfterEnd >= DAYS_FINALIZADO_TO_POSTVENTA) {
                newStage = 'postventa';
            } else if (lead.stage === 'postventa' && daysAfterEnd >= DAYS_TO_HISTORICO) {
                newStage = 'historico';
            }
        }

        if (!newStage) continue;

        const oldStage = lead.stage;
        lead.stage = newStage;
        lead.progress = calcProgress(newStage);

        const sid = lead._supabaseId;
        if (sid) {
            store._recentSyncs.add(sid);
            setTimeout(() => store._recentSyncs.delete(sid), 15000);
            window.supabaseDB?.updateLeadStage(sid, newStage, lead.progress).catch(() => {
                window.toast?.('Error actualizando etapa de ' + (lead.titular || 'lead'), 'error');
            });
        }

        if (newStage === 'historico') {
            lead.isHistorico = true;
            store.leads = store.leads.filter(l => l.id !== lead.id);
            store.historico.push(lead);
            if (sid) {
                window.supabaseDB?.updateLeadHistorico(sid);
            }
        }

        transitioned.push({ lead, oldStage, newStage });

        if (newStage === 'finalizado') {
            _createSurveyTarea(lead);
        }
    }

    return transitioned;
}

function _createSurveyTarea(lead) {
    const leadId = lead._supabaseId || lead.id;
    const existing = store.tareas.find(t =>
        (t.leadId === leadId || t.leadId === lead.id) && t.titulo?.startsWith('Enviar encuesta de satisfaccion')
    );
    if (existing) return;

    const tarea = {
        id: crypto.randomUUID(),
        titulo: 'Enviar encuesta de satisfaccion - ' + (lead.titular || ''),
        descripcion: 'El viaje de ' + (lead.titular || 'este cliente') + ' ha finalizado. Enviale el link de su dashboard para que llene la encuesta de satisfaccion.',
        responsable: lead.responsable || 'Sara',
        leadId,
        estado: 'pendiente',
        prioridad: 'alta',
        fechaLimite: new Date(Date.now() + DAYS_SURVEY_DEADLINE * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        creadoPor: 'Sistema'
    };

    store.tareas.push(tarea);
    window.syncTareaToSupabase?.(tarea);
}

// --- Auto-Create Client Dashboards ---
const DASHBOARD_STAGES = ['reserva_abono', 'pago_completo', 'documentacion', 'en_destino', 'finalizado', 'postventa'];

export async function autoCreateDashboards() {
    let created = 0;
    for (const lead of store.leads) {
        if (!DASHBOARD_STAGES.includes(lead.stage)) continue;
        const sid = lead._supabaseId;
        if (!sid || sid.length !== 36) continue;

        const existing = await window.supabaseDB?.fetchClientDashboard(sid);
        if (existing) continue;

        const prefill = {
            titular: lead.titular,
            destination: lead.destination,
            type: lead.type,
            travelDate: lead.travelDate,
            travelEnd: lead.travelEnd,
            reservationNum: lead.reservationNum,
            platform: lead.platform,
            hotels: lead.hotels,
            parkEntries: lead.parkEntries,
            cruises: lead.cruises,
            grupoViajeros: lead.grupoViajeros || lead.formData?.grupoViajeros,
        };
        const dash = await window.supabaseDB?.createClientDashboard(sid, prefill);
        if (dash) {
            store._dashboardCache[lead.id] = dash;
            created++;
        }
    }
    if (created > 0) {
        window.toast?.(created + ' dashboard' + (created > 1 ? 's' : '') + ' creado' + (created > 1 ? 's' : '') + ' para clientes confirmados', 'success');
    }
}

// Register on window for inline module access
Object.assign(window, {
    getType, calcularAbonoEsperado, calcProgress, getStageIndex,
    determineStageVentas, isFrequentTraveler, isFrequentFromAllTrips,
    getClientM2MTotal, getClientM2MTotalFull, isM2MTraveler, isProximoAViajar,
    isFullyPaidTrip, findDupByPhone, findDupByEmail, findDupByName, calcM2MFromTotal,
    ensureHotelsArray, ensureParkEntriesArray, ensureCruisesArray, sortByDate,
    runAutoTransitions, autoCreateDashboards
});
